<template>
  {{ formattedDeliveryDate }}
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api/src/standards/DateTime'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './DeliveryDate.translations'

const i18n = useI18n()

const {
  promisedMinDeliveryDate,
  promisedMaxDeliveryDate,
  estimatedDeliveryDate,
} = defineProps<{
  promisedMinDeliveryDate: DateTime
  promisedMaxDeliveryDate: DateTime
  estimatedDeliveryDate: DateTime | null
}>()

const isToday = (date: Date) => {
  const today = new Date()

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

const formattedPromisedDeliveryDateRange = computed(() => {
  const minDate = new Date(promisedMinDeliveryDate)
  const maxDate = new Date(promisedMaxDeliveryDate)

  return formatDateRange(minDate, maxDate)
})

const formattedDeliveryDate = computed(() => {
  if (estimatedDeliveryDate) {
    const date = new Date(estimatedDeliveryDate)

    return formatSingleDate(date)
  }

  return formattedPromisedDeliveryDateRange.value
})

function formatSingleDate(date: Date) {
  const weekday = isToday(date)
    ? i18n(translations.today)
    : i18n.date(date, { weekday: 'long' })
  const formattedDate = i18n.date(date, {
    month: 'short',
    day: 'numeric',
  })

  return i18n(translations.singleDeliveryDate, {
    weekday,
    date: formattedDate,
  })
}

function formatDateRange(minDate: Date, maxDate: Date) {
  const isSameMonth =
    minDate.getMonth() === maxDate.getMonth() &&
    minDate.getFullYear() === maxDate.getFullYear()

  if (isSameMonth) {
    const month = i18n.date(minDate, { month: 'short' })

    return i18n(translations.sameMonthDeliveryDateRange, {
      month,
      day1: minDate.getDate(),
      day2: maxDate.getDate(),
    })
  }

  const minFormattedDate = i18n.date(minDate, {
    month: 'short',
    day: 'numeric',
  })
  const maxFormattedDate = i18n.date(maxDate, {
    month: 'short',
    day: 'numeric',
  })

  return i18n(translations.splitMonthDeliveryDateRange, {
    date1: minFormattedDate,
    date2: maxFormattedDate,
  })
}
</script>
